import { CARET_PLACEMENTS, Message, PaymentProgress, Text, Tooltip } from '@afterpaytouch/core'
import React, { FunctionComponent, useEffect, useRef, useState } from 'react'
import { useInViewport } from 'react-in-viewport'
import { Money } from '../'
import { Trans } from '../../utils/convergence'
import {
  useDonation,
  useIsCheckoutLoadingOrUpdating,
  useCalculatedPaymentSchedules,
  usePaymentMessageContext,
  useIsPayIn2,
  useShowPaymentScheduleTooltip,
  useAdjustedDownPaymentMessagingEnabled,
  useCheckoutState,
  useCountry,
} from '../../state'
import { useCheckoutSizes } from '../../utils/responsive'
import clsx from 'clsx'

import styles from './style.module.css'
import { getNumberWithOrdinal } from './helper'
import { DueDateText } from './DueDateText'
import { useTranslation } from '../../utils/convergence'
import { useShowPaymentScheduleToast } from '../PaymentScheduleToast'
import { TrackingEvent } from '../../model'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { Country } from '@afterpay/types'

const PaymentMessage: FunctionComponent = (): JSX.Element => {
  const { started } = useCheckoutState()
  const context = usePaymentMessageContext()
  const adjustedDownPaymentEnabled = useAdjustedDownPaymentMessagingEnabled()
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  const downPaymentRiskLevel = started?.downPaymentRiskInfo?.downPaymentRiskLevel

  useEffect(() => {
    if (adjustedDownPaymentEnabled) {
      logEvent(TrackingEvent.VIEWED_ADJUSTED_DOWN_PAYMENT_MESSAGING, { riskLevel: downPaymentRiskLevel })
    }
  }, [adjustedDownPaymentEnabled])

  return (
    <Text renderAs='span' size='XS'>
      <Trans i18nKey='common:amountDueToday' context={context} />
    </Text>
  )
}

export const PaymentSchedule: FunctionComponent = () => {
  const { t } = useTranslation()
  const country = useCountry()
  const paymentProgressRef = useRef(null)
  const toolTipRef = useRef(null)
  const { inViewport } = useInViewport(paymentProgressRef, {}, { disconnectOnLeave: false }, {})
  const { harveyBallSize, harveyBallCaptionSize } = useCheckoutSizes()

  const isShippingOptionUpdating = useIsCheckoutLoadingOrUpdating()
  const { isUpdatingDonation } = useDonation()

  const adjustedDownPaymentEnabled = useAdjustedDownPaymentMessagingEnabled()
  const downPaymentContext = usePaymentMessageContext()
  const paymentSchedules = useCalculatedPaymentSchedules()
  const [steps, setSteps] = useState(paymentSchedules.map(() => 0))
  const isPayIn2 = useIsPayIn2()

  const shouldShowTooltip = useShowPaymentScheduleTooltip(adjustedDownPaymentEnabled)
  const shouldShowDownPaymentMessage = adjustedDownPaymentEnabled && country === Country.CA

  const paymentProgressWrapperStyles = clsx(steps.length % 3 === 0 ? styles.wrapperThreeDivisible : styles.wrapperFourDivisible)

  useShowPaymentScheduleToast()

  useEffect(() => {
    if (inViewport) {
      setSteps(paymentSchedules.map((_, index) => index + 1))
    }
    if (!inViewport) {
      setSteps(paymentSchedules.map(() => 0))
    }
    return () => {
      setSteps(paymentSchedules.map(() => 0))
    }
  }, [inViewport])

  return (
    <>
      <div ref={paymentProgressRef} className={paymentProgressWrapperStyles}>
        {steps.map((step, index) => {
          const isLoading = isShippingOptionUpdating || (index === 0 && isUpdatingDonation)
          const title = `${getNumberWithOrdinal(step)} ${t('common:installment')}`
          const itemClsx = clsx(styles.item, { [styles.firstItem]: steps.length === 2 && index === 0 })

          return (
            <div key={`paymentScheduleItem-${index}`} className={itemClsx} {...(index === 0 && { ref: toolTipRef })}>
              <PaymentProgress.Circle size={harveyBallSize} step={isLoading ? 0 : step} maxStep={steps.length} title={title} paymentLabel={step.toString()} />
              <div className={styles.moneyWrapper} data-testid={`paymentScheduleItem-money-${index}`}>
                <Money bold={index === 0} value={paymentSchedules?.[index]?.amount} size={harveyBallCaptionSize} loading={isLoading} />
              </div>
              <Text color='Gray40' size={harveyBallCaptionSize} renderAs='div' bold={index === 0} data-testid={`paymentScheduleItem-date-${index}`}>
                <DueDateText key={index} index={index} dueDate={paymentSchedules[index]?.installmentDueDate} dateTimeFormatOptions={{ weekday: 'short' }} />
              </Text>
            </div>
          )
        })}
      </div>
      {shouldShowTooltip && (
        <div className={styles.tooltipWrapper}>
          <Tooltip
            id='payment-schedule-notification-tooltip'
            triggerRef={toolTipRef}
            caretPlacement={isPayIn2 ? CARET_PLACEMENTS.BOTTOM : CARET_PLACEMENTS.BOTTOM_START}
            testNameSpace='tooltip'
            usePortal={false}
            content={<PaymentMessage />}
            bubbleSize={{ width: '100%' }}
            inline
          />
        </div>
      )}
      {shouldShowDownPaymentMessage && (
        <div className={styles.downPaymentMessageWrapper}>
          <Message
            kind='Basic'
            description={t('common:amountDueToday', { context: downPaymentContext, count: steps.length - 1 })}
            testNameSpace='adjusted-down-payment'
          />
        </div>
      )}
    </>
  )
}
